import {
  useCallback,
  useLayoutEffect,
  useRef,
  type ClipboardEvent,
  type FocusEvent,
} from "react";
import clsx from "clsx";

type HtmlNotesEditorProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minRows?: number;
  "aria-label"?: string;
};

function isVisuallyEmptyHtml(html: string): boolean {
  const text = html
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]+>/g, "")
    .trim();
  return !text;
}

/**
 * Einfacher HTML-Notiz-Editor: rendert Tags sichtbar und wächst mit dem Inhalt.
 * Speichert weiterhin HTML (passend zu migrierten Artikel-Beschreibungen).
 */
export function HtmlNotesEditor({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  minRows = 4,
  "aria-label": ariaLabel,
}: HtmlNotesEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);
  const lastHtmlRef = useRef(value || "");

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el || disabled) return;
    const next = el.innerHTML;
    if (next === lastHtmlRef.current) return;
    lastHtmlRef.current = next;
    onChange(next);
  }, [disabled, onChange]);

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || focusedRef.current) return;
    const next = value || "";
    if (next === lastHtmlRef.current && el.innerHTML === next) return;
    lastHtmlRef.current = next;
    el.innerHTML = next;
  }, [value]);

  function applyFormat(command: "bold" | "italic" | "underline") {
    if (disabled) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(command, false);
    emitChange();
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    const html = event.clipboardData.getData("text/html");
    if (html) return;
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    emitChange();
  }

  function handleBlur(_event: FocusEvent<HTMLDivElement>) {
    focusedRef.current = false;
    emitChange();
  }

  const showPlaceholder = isVisuallyEmptyHtml(value || "");

  return (
    <div className={clsx("eh-html-notes", className)}>
      <div
        className="eh-html-notes__toolbar"
        role="toolbar"
        aria-label="Textformatierung"
        onMouseDown={(event) => event.preventDefault()}
      >
        <button
          type="button"
          className="eh-html-notes__toolbar-btn font-bold"
          title="Fett"
          disabled={disabled}
          onClick={() => applyFormat("bold")}
        >
          B
        </button>
        <button
          type="button"
          className="eh-html-notes__toolbar-btn italic"
          title="Kursiv"
          disabled={disabled}
          onClick={() => applyFormat("italic")}
        >
          I
        </button>
        <button
          type="button"
          className="eh-html-notes__toolbar-btn underline"
          title="Unterstrichen"
          disabled={disabled}
          onClick={() => applyFormat("underline")}
        >
          U
        </button>
      </div>
      <div
        ref={editorRef}
        className={clsx(
          "eh-html-notes__editor eh-input",
          disabled && "eh-html-notes__editor--disabled",
          showPlaceholder && "eh-html-notes__editor--empty",
        )}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-label={ariaLabel || "Interne Notizen"}
        aria-multiline
        data-placeholder={placeholder}
        style={{ ["--eh-html-notes-min-rows" as string]: String(minRows) }}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onBlur={handleBlur}
        onInput={emitChange}
        onPaste={handlePaste}
        onMouseDown={(event) => event.stopPropagation()}
      />
    </div>
  );
}
